import { useRouter } from 'next/compat/router';
import Link from 'next/link';
import { type AnchorHTMLAttributes, forwardRef, type ReactNode } from 'react';

// Known sibling zone basePaths across the monorepo. Must match the `basePath`
// declared in each app's next.config.js. Core uses '' (root) and owns every
// URL that doesn't start with one of these prefixes.
const SIBLING_ZONE_BASE_PATHS = [
	'/help',
	'/account',
	'/players',
	'/search',
	'/store',
] as const;

type SmartLinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & {
	href?: string;
	external?: boolean;
	children: ReactNode;
};

const startsWithPath = (href: string, prefix: string) =>
	href === prefix || href.startsWith(`${prefix}/`);

export const SmartLink = forwardRef<HTMLAnchorElement, SmartLinkProps>(
	({ href, external, target, children, ...rest }, ref) => {
		const router = useRouter();
		const basePath = router?.basePath ?? '';

		const isAbsolute = !!href && /^(?:[a-z]+:)?\/\//i.test(href);
		const isHashOrMail = !!href && /^(mailto:|tel:|#)/.test(href);
		const forceHard =
			external || target === '_blank' || isAbsolute || isHashOrMail;

		if (!href || forceHard) {
			return (
				<a ref={ref} href={href} target={target} {...rest}>
					{children}
				</a>
			);
		}

		// A URL is owned by the current zone only if:
		//   - it starts with the current basePath (non-root apps), OR
		//   - basePath is '' (core) and the URL doesn't start with any sibling zone.
		const ownedByCurrentZone =
			basePath !== ''
				? startsWithPath(href, basePath)
				: href.startsWith('/') &&
				  !SIBLING_ZONE_BASE_PATHS.some((zone) =>
						startsWithPath(href, zone)
				  );

		if (!ownedByCurrentZone) {
			return (
				<a ref={ref} href={href} target={target} {...rest}>
					{children}
				</a>
			);
		}

		// Next.js Link auto-prepends the host's basePath, so strip it first.
		const stripped =
			basePath && href.startsWith(basePath)
				? href.slice(basePath.length) || '/'
				: href;

		return (
			<Link ref={ref} href={stripped} target={target} {...rest}>
				{children}
			</Link>
		);
	}
);

SmartLink.displayName = 'SmartLink';

export default SmartLink;
